

import { useEffect, useReducer, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import DataWrapper from 'interfaces/DataWrapper';
import { Entities } from 'models/Entities';
import { ErrorCause } from 'models/ErrorResponse';
import { HttpMethod } from 'models/HttpMethod';

import CmsPostSearchOperators from '../interfaces/CmsPostSearchOperators';
import DataSet from '../interfaces/DataSet';
import DataSetReducerMessage from '../interfaces/DataSetReducerMessage';
import { CmsPage } from '../models/CmsPage';
import { ImportResponse } from '../models/ImportResponse';
import { ApiService } from '../services/ApiService';

export const useCmsPostSearch = (
  apiBaseUrl: string,
  onTokenExpired: () => void,
  onLoaded?: (cmsPages: CmsPage[] | DataWrapper<CmsPage>) => void,
) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const scheduleService = new ApiService<CmsPage>(apiBaseUrl, Entities.CmsPost);
  const defaultOperators: CmsPostSearchOperators = {
    SearchTerms: '',
  };
  const [operators, setOperators] = useState<CmsPostSearchOperators>(defaultOperators);

  const defaultApiResponse: DataSet<CmsPage> = {
    responseData: [],
    isLoading: true,
    isError: false,
    error: undefined,
  };

  const apiResponseReducer = (
    _priorApiResponse: DataSet<CmsPage>,
    fieldUpdate: DataSetReducerMessage,
  ): DataSet<CmsPage> => {
    const updatedApiResponse: DataSet<CmsPage> = { ..._priorApiResponse };
    (updatedApiResponse as any)[fieldUpdate.type] = fieldUpdate.value;

    switch (fieldUpdate.type) {
      case 'responseData':
        updatedApiResponse.isLoading = false;
        updatedApiResponse.error = undefined;
        updatedApiResponse.isError = false;
        break;
      case 'error':
        updatedApiResponse.isError = true;
        updatedApiResponse.isLoading = false;
        break;
      default:
        break;
    }

    return updatedApiResponse;
  };

  const [cmsPages, setCmsPages] = useReducer(apiResponseReducer, defaultApiResponse);

  useEffect(() => {
    if (!isAuthenticated || isLoading || !operators.SearchTerms) {
      return;
    }

    let data: CmsPage[] | DataWrapper<CmsPage> = [];

    const fetchData = async () => {
      const token = await getAccessTokenSilently();
      if (!token) {
        setCmsPages({ type: 'error', value: new Error('401 unauthorized', { cause: ErrorCause.AuthenticationError }) });
      } else {
        scheduleService.setAccessToken(token);
        data = await scheduleService.all(operators, 'Search');
        // keep this commented.  Seems to be the reason why all datasets load twice.
        // keeping line below for historical reasons in case in other areas of code it starts misbehaving.
        // setCmsPages({ type: 'operators', value: operators });
        setCmsPages({ type: 'responseData', value: data });
      }
    };

    fetchData()
      .catch((e) => {
        setCmsPages({ type: 'error', value: e });
      })
      .finally(() => {
        if (onLoaded) {
          onLoaded(data);
        }
      });
  }, [operators, isAuthenticated, isLoading]);

  const searchPosts = (terms: string, projectId: number) => {
    if (terms) {
      const ops = { ...operators };
      ops.ProjectId = projectId;
      ops.SearchTerms = terms;
      setOperators(ops);
    }
  };

  const importPost = async (projectId: number, postId: number): Promise<ImportResponse> => {
    const token = await getAccessTokenSilently();
    if (!token) {
      onTokenExpired();
    } else {
      scheduleService.setAccessToken(token);
      const response = await scheduleService.execute(
        `${postId}/Import?ProjectId=${projectId}`,
        HttpMethod.POST,
      );
      if (response.ok) {
        return (await response.json()) as ImportResponse;
      }
    }
    throw new Error(`Failed to import CMS post id: ${postId}`);
  };

  return { cmsPages, searchPosts, importPost };
};
